

import axios from 'axios';
import { Sidebar } from './Sidebar';
import React, { useContext, useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { Header } from './Header';
import MarqueeBottom from './components/MarqueeBottom';
import UnavailablePanel from './components/UnavailablePanel';
import WhatsAppButton from './components/WhatsAppButton';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import LoginReg from './LoginReg';
import { AuthContext } from '../context/Authcontext';
import '../assets/AdminCss/games.css';
import { useTheme } from '../context/ThemeContext';

const base_url = process.env.REACT_APP_API_URL;

// ─── Constants (defined once, never recreated) ────────────────────────────────

/* Categories, not game names or provider allow-lists.
   The vendor tags every game with a path like CASINO/SLOT/5REEL,
   CASINO/LIVECASINO/ROULETTE or CASINO/CRASH -- 95 of them across the 14,956
   games the catalogue returns. The old filters ignored that and matched on
   English words in the title, or on hand-typed provider names, which is why
   whole tabs were empty:

   - Crash Games looked for names containing 'Aviator', 'JetX' or 'Crash'.
     Not one of the 14,956 game names contains any of those three, while 198
     games sit in CASINO/CRASH. The tab could only ever be empty.
   - Jili matched provider 'Jili'; the feed says 'JiLi' (capital L), so its
     232 games never matched either.
   - 14 of the 35 provider names in the two lists below did not exist in the
     feed at all -- some truncated ('Nolimit Ci', 'Dragoon So', 'Hacksaw Ga',
     'JiLi Gamin'), others misspelled ("Play'n Go" for "Play'n GO", 'AVATAR UX'
     for 'AvatarUX') -- so Live Casino and Slots were quietly partial too.

   Matching a category substring survives the vendor adding a sub-category and
   is immune to casing and to provider renames. */
const inCategory = (game, pattern) => pattern.test(game?.category || '');
const nameMatches = (game, pattern) => pattern.test(game?.name || '');

const TABS = [
  { index: 0,  label: 'All Casino' },
  { index: 21, label: 'All Games' },
  { index: 4,  label: 'Teen Patti' },
  { index: 5,  label: 'Indian Casinos' },
  { index: 16, label: 'Slot Games' },
  { index: 17, label: 'AE Sexy' },
  { index: 18, label: 'Table Games' },
  { index: 7,  label: 'Live Casinos' },
  // The drawer links straight to list 6; without a tab the category strip
  // showed nothing selected, which read as "the click did nothing".
  { index: 6,  label: 'Jili Games' },
  { index: 9,  label: 'Roulette' },
  { index: 10, label: 'Baccarat' },
  { index: 11, label: 'Crash Games' },
  // { index: 12, label: 'Sic Bo' },
  { index: 13, label: 'Lightning' },
  { index: 14, label: 'Poker' },
  { index: 15, label: 'Blackjack' },
  { index: 22, label: 'Providers' },
];

// ─── Filter only the active tab — called once on tab switch or when games grow ─

const filterGames = (games, list) => {
  switch (list) {
    case 0:  return games;
    // Teen Patti lives under both LIVECASINO and TABLEGAME.
    case 4:  return games.filter(g =>
               inCategory(g, /TEEN_PATTI/i) || nameMatches(g, /teen\s*patti/i));
    // The Indian table games, by category rather than by English title.
    case 5:  return games.filter(g =>
               inCategory(g, /TEEN_PATTI|ANDAR_BAHAR|RUMMY|JHANDI_MUNDA|LUDO/i) ||
               nameMatches(g, /teen\s*patti|andar\s*bahar|rummy|jhandi/i));
    case 6:  return games.filter(g => /jili/i.test(g?.provider?.name || ''));
    case 7:  return games.filter(g => inCategory(g, /^CASINO\/LIVECASINO/i));
    case 9:  return games.filter(g => inCategory(g, /ROULETTE/i));
    case 10: return games.filter(g => inCategory(g, /BACCARAT/i));
    case 11: return games.filter(g => inCategory(g, /CRASH/i));
    case 12: return games.filter(g => inCategory(g, /SICBO/i));
    // No category for this one -- Lightning is a game-title family.
    case 13: return games.filter(g => nameMatches(g, /lightning/i));
    case 14: return games.filter(g => inCategory(g, /POKER/i));
    case 15: return games.filter(g => inCategory(g, /BLACKJACK/i));
    case 16: return games.filter(g => inCategory(g, /^CASINO\/SLOT/i));
    // 21 -- everything that is not a dealer at a table: slots, crash, the
    // instant-win and game-show families. The Games menu needs a landing
    // category, and list 0 could not serve as one because it is literally
    // everything, which is already what All Casino shows.
    case 21: return games.filter(g =>
               inCategory(g, /^CASINO\/(SLOT|CRASH|INSTANTWIN|GAME_SHOW|SHOOTING|LOTTERY)/i));
    case 17: return games.filter(g => nameMatches(g, /sexy/i));
    case 18: return games.filter(g => inCategory(g, /^CASINO\/TABLEGAME/i));
    case 19: return games.filter(g => /dragoon/i.test(g?.provider?.name || ''));
    case 20: return games.filter(g => /dragoon/i.test(g?.provider?.name || ''));
    default: return games;
  }
};

// ─── GameGrid ─────────────────────────────────────────────────────────────────

const GameGrid = ({ games, token, isLoading, searchText, loadError, onRetry, retrying }) => {
  const [active, setActive]       = useState(false);
  const [regactive, setregactive] = useState(false);
  const [displayCount, setDisplayCount] = useState(66);
  const [isSearching, setIsSearching]   = useState(false);
  const searchTimeoutRef = useRef(null);
  const { isAuthenticated } = useContext(AuthContext);

  // Reset pagination when games list changes (tab switch)
  useEffect(() => { setDisplayCount(66); }, [games]);

  // Debounced search filter — only runs when searchText is active
  const filteredGames = useMemo(() => {
    if (!searchText) return games;
    const lower = searchText.toLowerCase();
    return games.filter(g =>
      g?.name?.toLowerCase().includes(lower) ||
      g?.provider?.name?.toLowerCase().includes(lower)
    );
  }, [games, searchText]);

  const visibleGames = useMemo(
    () => filteredGames.slice(0, displayCount),
    [filteredGames, displayCount]
  );

  /* Every failure here used to read "Unable to launch game", including an
     expired session and a game the provider will not serve -- and a reply of
     success with no url sent the browser to /undefined, i.e. a blank page.
     Same handling as the home page's openGameById. */
  const openGameById = useCallback(async (gameId) => {
    if (!gameId) {
      toast.error('This game is not available right now. Please try again in a moment.');
      return;
    }
    try {
      const res = await axios.post(
        base_url + '/api/gamelaunch/url',
        { token, gameId },
        { withCredentials: true, headers: { 'content-type': 'application/json' } }
      );
      if (res?.data?.success && res.data.url) {
        window.location.href = res.data.url;
      } else if (/login/i.test(res?.data?.message || '')) {
        toast.error('Please Login to play!');
        setActive(true);
      } else {
        toast.error(res?.data?.message || 'Could not open the game. Please try again.');
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast.error('Please Login to play!');
        setActive(true);
      } else {
        toast.error(
          err?.response?.data?.message || 'Could not open the game. Please try again.'
        );
      }
    }
  }, [token]);

  // Image plus a metadata strip at one fixed aspect ratio -- the same card
  // the home page uses. It was a bare image with a full-bleed LOGIN/PLAY
  // overlay and no title, so the grid was a wall of unlabelled artwork.
  const gameCards = useMemo(() => visibleGames.map((game, index) => (
    <button
      type="button"
      key={game?.id || index}
      className="rb-game"
      onClick={() => { if (isAuthenticated) openGameById(game?.id); else setActive(true); }}
    >
      <span className="rb-game__art">
        <img
          src={game?.images?.[2]?.url || game?.images?.[0]?.url || '/placeholder.jpg'}
          alt={game?.name || 'Casino game'}
          loading="lazy"
          decoding="async"
        />
        <span className="rb-game__play" aria-hidden="true">
          <i className="fa fa-play" /> {isAuthenticated ? 'Open' : 'Log in'}
        </span>
      </span>
      <span className="rb-game__meta">
        <span style={{ minWidth: 0 }}>
          <span className="rb-game__name">{game?.name || 'Game'}</span>
          <span className="rb-game__cat">
            {game?.provider?.name || game?.category || 'Casino'}
          </span>
        </span>
        <span className="rb-game__arrow" aria-hidden="true">&rarr;</span>
      </span>
    </button>
  )), [visibleGames, isAuthenticated, openGameById]);

  if (isLoading) {
    return (
      <div className="upcoming-container">
        <div className="rb-games" style={{ marginTop: 'var(--rb-4)' }}>
          {Array.from({ length: 15 }).map((_, i) => (
            <div
              key={'skel' + i}
              className="rb-skeleton"
              style={{ aspectRatio: '16 / 13' }}
              aria-hidden="true"
            />
          ))}
        </div>
        <span className="rb-visually-hidden" role="status">Loading games</span>
      </div>
    );
  }

  return (
    <>
      <LoginReg active={active} setactive={setActive} regactive={regactive} setregactive={setregactive} />
      <div className="upcoming-container">
        {!filteredGames.length && searchText ? (
          <div className="rb-empty" style={{ marginTop: 'var(--rb-4)' }}>
            <span className="rb-empty__icon" aria-hidden="true">
              <i className="fa fa-search" />
            </span>
            <span className="rb-empty__title">No games match that search</span>
            <span className="rb-empty__body">
              Check the spelling, or try a provider name such as Evolution or Jili.
            </span>
          </div>
        ) : null}
        {!games.length && !searchText ? (
          <div style={{ marginTop: 'var(--rb-4)' }}>
            <UnavailablePanel
              title={
                loadError
                  ? "Casino games aren't loading"
                  : 'No games in this category yet'
              }
              body={
                loadError
                  ? "Our games come from an outside provider, and we can't reach them at the moment. Sports betting is running normally."
                  : 'Nothing has been listed here so far. Try another category from the strip above.'
              }
              onRetry={loadError ? onRetry : undefined}
              retrying={retrying}
              secondaryLabel="Go to sports"
              onSecondary={() => (window.location.href = '/sportall')}
              note={loadError ? 'Nothing is wrong with your account or your balance.' : undefined}
            />
          </div>
        ) : null}
        {gameCards.length > 0 && (
          <div className="rb-games" style={{ marginTop: 'var(--rb-4)' }}>
            {gameCards}
          </div>
        )}
        {visibleGames.length < filteredGames.length && (
          <div className="text-center" style={{ margin: 'var(--rb-5) 0' }}>
            <button
              type="button"
              onClick={() => setDisplayCount(p => p + 66)}
              className="rb-btn rb-btn--secondary"
            >
              Load more games
            </button>
          </div>
        )}
      </div>
    </>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const CasinoGames = () => {
  const isInitialDesktop = window.innerWidth > 1100;
  const location = useLocation();

  // Was `useState(isInitialDesktop)`, so the menu was open the moment the
  // page loaded on a desktop -- including every arrival from the nav's
  // Live Casino link. The menu is something the visitor opens.
  const [isSidebar, setSidebar] = useState(false);
  const [isDesktop, setIsDesktop] = useState(isInitialDesktop);
  const [list, setList]           = useState(location?.state?.list || 0);
  const [searchText, setSearchText] = useState('');
  const [showCanvas, setShowCanvas] = useState(false);
  const [token, setToken]           = useState();

  // ✅ ONE state: raw flat array. Nothing else.
  const [games, setGames]   = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Ref so SSE closure always appends to latest array without stale state
  const gamesRef = useRef([]);
  // Bumped by the retry button so the stream effect runs again.
  const [gamesAttempt, setGamesAttempt] = useState(0);

  const [retrying, setRetrying] = useState(false);

  const retryGames = () => {
    gamesRef.current = [];
    setGames([]);
    setLoadError(false);
    setIsLoading(true);
    setGamesAttempt((n) => n + 1);
    // A failing stream answers in about 40ms on localhost; without a floor
    // the button flickers and the click looks ignored.
    setRetrying(true);
    setTimeout(() => setRetrying(false), 700);
  };

  // ✅ Filter only current tab — computed from raw games, not stored
  const currentGames = useMemo(() => filterGames(games, list), [games, list]);

  // Same pill as the home page sport tabs, so the two category strips in
  // the product read as one control rather than two separate designs.
  const tabClass = (index) =>
    list === index ? 'sport-tab sport-tab--active' : 'sport-tab';

  const scrollTabs = (dir) => {
    const el = document.getElementById('casinoTabs');
    if (el) el.scrollBy({ left: dir === 'left' ? -200 : 200, behavior: 'smooth' });
  };

  // Responsive sidebar
  useEffect(() => {
    const onResize = () => {
      const desktop = window.innerWidth > 1100;
      setIsDesktop(desktop);
      // Only ever close on resize. This used to re-open the menu on every
      // resize event, and it ran once on mount, which is what forced the
      // panel open on arrival.
      if (!desktop) setSidebar(false);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Sync tab from router state
  useEffect(() => {
    if (location?.state?.list !== undefined) setList(location.state.list);
    // Arriving from the header search. setSearchText had no caller before
    // this, so the filter behind it was unreachable.
    if (location?.state?.q !== undefined) setSearchText(location.state.q);
  }, [location.state]);

  // Fetch via SSE
  useEffect(() => {
    window.scrollTo(0, 0);

    const eventSource = new EventSource(base_url + '/api/valid/token', { withCredentials: true });

    eventSource.addEventListener('message', (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.token) setToken(data.token);

        if (data.chunk && Array.isArray(data.chunk)) {
          // ✅ Append into ref first (no stale closure), then flush to state once
          gamesRef.current = gamesRef.current.concat(data.chunk);
          setGames(gamesRef.current);   // single setState, single re-render
          setIsLoading(false);
        }

        if (data.complete || data.error) {
          // The stream is finished. Clear loading whatever the outcome:
          // /api/valid/token answers {"error":"Something went wrong"} over a
          // 200 when there is no auth, so this path is reached with no chunk
          // ever sent -- which left the grid spinning forever.
          if (data.error) setLoadError(true);
          setIsLoading(false);
          eventSource.close();
        }

      } catch (e) {
        console.error('SSE parse error', e);
        setLoadError(true);
        setIsLoading(false);
      }
    });

    eventSource.onerror = () => {
      setLoadError(true);
      setIsLoading(false);
      eventSource.close();
    };

    return () => {
      eventSource.close();       // cleanup on unmount
      gamesRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gamesAttempt]);

  return (
    <app-root _nghost-jrx-c101="" ng-version="12.2.16">
      <div _ngcontent-jrx-c101="">
        <app-toasts _ngcontent-jrx-c101="" aria-live="polite" aria-atomic="true" className="ngb-toasts" />
        <router-outlet _ngcontent-jrx-c101="" />
        <app-landing-page-new _nghost-jrx-c95="" className="ng-star-inserted">
          <div _ngcontent-jrx-c95="" className="head-container gamess">

            <Header
              isSidebar={isSidebar}
              setSidebar={setSidebar}
              isDesktop={isDesktop}
              changeCasinoTab={setList}
              show={showCanvas}
              handleShow={() => setShowCanvas(true)}
              handleClose={() => setShowCanvas(false)}
            />

            <div
              _ngcontent-jrx-c95=""
              className="body-container mb-5"
              style={{ display: isDesktop ? 'flex' : 'block', position: 'relative' }}
            >
              <Sidebar isSidebar={isSidebar} setSidebar={setSidebar} isDesktop={isDesktop} />

              <div
                _ngcontent-jrx-c95=""
                className="rightbar"
                style={{
                  width: isDesktop ? (isSidebar ? '84%' : '100%') : '100%',
                  transition: 'width 0.3s ease-in-out',
                  backgroundColor: 'var(--surface-panel)',
                }}
              >
                <app-upcoming _ngcontent-jrx-c95="" _nghost-jrx-c94="" className="ng-star-inserted">

                  {/* Category tabs. Was a bright blue slab with two grey
                      gradient arrow buttons that belonged to no palette.
                      Now the same sticky pill strip the sports tabs use. */}
                  <div className="rb-shell">
                    <div className="rb-sportbar rb-casinobar">
                      <button
                        type="button"
                        className="rb-casinobar__arrow"
                        aria-label="Scroll categories left"
                        onClick={() => scrollTabs('left')}
                      >
                        <i className="fa fa-chevron-left" aria-hidden="true" />
                      </button>

                      <ul
                        id="casinoTabs"
                        className="sport-tabs"
                        role="tablist"
                        aria-label="Casino categories"
                        style={{ listStyle: 'none', margin: 0, padding: 0, flex: 1 }}
                      >
                        {TABS.map(({ index, label }) => (
                          <li key={index} style={{ display: 'contents' }}>
                            <button
                              type="button"
                              role="tab"
                              aria-selected={list === index}
                              className={tabClass(index)}
                              onClick={() => setList(index)}
                            >
                              {label}
                            </button>
                          </li>
                        ))}
                      </ul>

                      <button
                        type="button"
                        className="rb-casinobar__arrow"
                        aria-label="Scroll categories right"
                        onClick={() => scrollTabs('right')}
                      >
                        <i className="fa fa-chevron-right" aria-hidden="true" />
                      </button>
                    </div>

                    {searchText ? (
                      <div className="rb-filters" style={{ marginTop: 'var(--rb-3)' }}>
                        <span className="rb-chip rb-chip--active">
                          Search: {searchText}
                          <button
                            type="button"
                            aria-label="Clear search"
                            onClick={() => setSearchText('')}
                            style={{
                              background: 'none',
                              border: 0,
                              color: 'inherit',
                              cursor: 'pointer',
                              padding: 0,
                              marginLeft: 4,
                            }}
                          >
                            <i className="fa fa-times" aria-hidden="true" />
                          </button>
                        </span>
                      </div>
                    ) : null}
                  </div>

                </app-upcoming>

                {/* ── Content ── */}
                {list === 22 ? (
                  <Providers games={games} />
                ) : (
                  <GameGrid
                    key={list}
                    games={currentGames}
                    token={token}
                    isLoading={isLoading}
                    searchText={searchText}
                    loadError={loadError}
                    onRetry={retryGames}
                    retrying={retrying}
                  />
                )}

              </div>
            </div>
            <MarqueeBottom onMenuClick={() => setShowCanvas(true)} />
          </div>
          <WhatsAppButton />
        </app-landing-page-new>
        <div className="slider-container" />
      </div>
    </app-root>
  );
};

export default CasinoGames;


// ─── Providers ────────────────────────────────────────────────────────────────

const PROVIDER_LOGOS = {
  'SPRIBE':                '/assets/providers/SPB.png',
  'EVOLUTION':             '/assets/providers/Evolotion.png',
  'EZUGI':                 '/assets/providers/Ezugi.png',
  'QTECH':                 '/assets/providers/qt.png',
  "PLAY'N GO":             '/assets/providers/play.png',
  'RED TIGER':             '/assets/providers/RED.png',
  'EVOPLAY':               '/assets/providers/EVP.png',
  'NETENT':                '/assets/providers/NET.png',
  'CQ9 CASINO':            '/assets/providers/CQC.png',
  'BLUEPRINT GAMING':      '/assets/providers/Blueprint.png',
  'YGGDRASIL':             '/assets/providers/YGG.png',
  'HABANERO':              '/assets/providers/HAB.png',
  'HACKSAW GAMING':        '/assets/providers/HAK.png',
  'PLAYPEARLS':            '/assets/providers/Playp.png',
  'GAMEART':               '/assets/providers/GameArt.png',
  'QUICKSPIN':             '/assets/providers/Quickspin.png',
  'JILI GAMING':           '/assets/providers/JiLi.png',
  '1X2 GAMING':            '/assets/providers/1x2.png',
  'RELAX GAMING':          '/assets/providers/RLX.png',
  'NOLIMIT CITY':          '/assets/providers/NLC.png',
  'ONETOUCH':              '/assets/providers/Onetouch.png',
  'DRAGOON SOFT':          '/assets/providers/Dragoon.png',
  'THUNDERKICK':           '/assets/providers/TK.png',
  'IRON DOG STUDIO':       '/assets/providers/IDS.png',
  'TRIPLE PG':             '/assets/providers/TPG.png',
  'GAMZIX':                '/assets/providers/GZX.png',
  'NETGAME ENTERTAINMENT': '/assets/providers/NGE.png',
  'WOOHOO GAMES':          '/assets/providers/WOO.png',
  'AVATAR UX':             '/assets/providers/_AUX.png',
  'FANTASMA GAMES':        '/assets/providers/FNG.png',
  'REVOLVER GAMING':       '/assets/providers/RG.png',
  'FUGASO':                '/assets/providers/FUG.png',
  'SKYWIND LIVE':          '/assets/providers/SWL.png',
};

const Providers = ({ games = [] }) => {
  const providerList = useMemo(() => {
    const counts = new Map();
    for (const g of games) {
      const raw = g?.provider?.name;
      if (!raw) continue;
      const key = String(raw).trim().toUpperCase();
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([name, count]) => ({
        name,
        count,
        src: PROVIDER_LOGOS[name] || '/placeholder.jpg',
      }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [games]);

  if (!providerList.length) {
    return (
      <div className="text-center my-4">
        <p className="text-white">No providers available yet.</p>
      </div>
    );
  }

  return (
    <div className="main-container px-5">
      <div className="row">
        {providerList.map((p) => (
          <div key={p.name} className="col-md-4 col-lg-3 mb-3">
            <div className="d-flex flex-column rounded p-3" style={{ background: '#393939' }}>
              <div className="text-center py-5">
                <img src={p.src} height={80} alt={p.name} className="custom" />
              </div>
              <span className="text-white px-2 fw-bold">{p.name}</span>
              <span className="text-white px-2">Total Games: {p.count}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};